import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import PlayerMergePanel from "../components/PlayerMergePanel";
import PlayerOwnAccessPanel from "../components/PlayerOwnAccessPanel";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { bulkErrorGroupLine, summarizeBulkErrors, type BulkErrorGroup } from "../lib/bulkUploadErrors";
import { altaManualNuevoJugadorError } from "../lib/altaManualValidation";
import FieldHint from "../components/FieldHint";
import TableScrollWrap from "../components/TableScrollWrap";
import {
  api,
  ApiError,
  type PlayerProfile,
} from "../lib/api";
import { isSupportUser, SUPPORT_ROLE_LABEL } from "../lib/api";
import { routes } from "../routes";
import { impersonatePlayer } from "../lib/playerImpersonation";
import {
  archiveSuccessMessage,
  familyAccountPinConflict,
  pinSuccessMessage,
  resolveFamilyAccountPin,
} from "../lib/familyAccount";
import { formatDate } from "../lib/dateFormat";
import { useMediaQuery } from "../lib/useMediaQuery";
import {
  accountNotOwnedEmailNote,
  accountNotOwnedInfo,
  accountNotOwnedLabel,
  accountNotOwnedListNote,
  accountNotOwnedNote,
  canManagePlayerAccount,
} from "../lib/playerAccountOwnership";

type PlayerEditForm = {
  first_name: string;
  last_name: string;
  maternal_last_name: string;
  unique_player_id: string;
  phone: string;
  email: string;
  city: string;
  state_province: string;
  gender: string;
  birthdate: string;
  nationality_code: string;
  national_federation_id: string;
  wtn_tennis_id: string;
  dominant_hand: string;
  backhand_type: string;
  club_name: string;
  guardian_name: string;
  guardian_phone: string;
  guardian_relationship: string;
  guardian_email: string;
  coach_name: string;
  coach_phone: string;
  coach_email: string;
  data_consent: boolean;
  category_id: string;
};

function editFormFromPlayer(player: PlayerProfile): PlayerEditForm {
  return {
    first_name: player.first_name,
    last_name: player.last_name ?? "",
    maternal_last_name: player.maternal_last_name ?? "",
    unique_player_id: player.unique_player_id ?? "",
    phone: player.phone ?? "",
    email: player.email ?? "",
    city: player.city ?? "",
    state_province: player.state_province ?? "",
    gender: player.gender ?? "",
    birthdate: player.birthdate ?? "",
    nationality_code: player.nationality_code ?? "",
    national_federation_id: player.national_federation_id ?? "",
    wtn_tennis_id: player.wtn_tennis_id ?? "",
    dominant_hand: player.dominant_hand ?? "",
    backhand_type: player.backhand_type ?? "",
    club_name: player.club_name ?? "",
    guardian_name: player.guardian_name ?? "",
    guardian_phone: player.guardian_phone ?? "",
    guardian_relationship: player.guardian_relationship ?? "",
    guardian_email: player.guardian_email ?? "",
    coach_name: player.coach_name ?? "",
    coach_phone: player.coach_phone ?? "",
    coach_email: player.coach_email ?? "",
    data_consent: player.data_consent,
    category_id: player.primary_category ? String(player.primary_category.id) : "",
  };
}


function displayValue(value: string | null | undefined) {
  return value?.trim() ? value : "—";
}

function formatGenderLabel(value: string | null | undefined) {
  if (value === "male") return "Masculino";
  if (value === "female") return "Femenino";
  return displayValue(value);
}

function formatHandLabel(value: string | null | undefined) {
  if (value === "right") return "Diestro";
  if (value === "left") return "Zurdo";
  return displayValue(value);
}

function formatBackhandLabel(value: string | null | undefined) {
  if (value === "one_hand") return "Una mano";
  if (value === "two_hands") return "Dos manos";
  return displayValue(value);
}

function formatConsentLabel(value: boolean | null | undefined) {
  return value ? "Sí" : "No";
}

/** «Con cuenta» = tiene usuario ligado para entrar (o, si el admin no la
 * maneja, el servidor dice que ya tiene con qué entrar). */
function playerHasAccount(p: PlayerProfile): boolean {
  return p.user_id != null || Boolean(p.username) || p.has_account === true;
}

function playerInitials(p: PlayerProfile): string {
  const a = p.first_name?.trim()?.[0] ?? "";
  const b = (p.last_name ?? "").trim()[0] ?? "";
  return (a + b).toUpperCase() || "?";
}

function playerContact(p: PlayerProfile): string {
  return p.email?.trim() || p.phone?.trim() || "Sin datos";
}

type AccountFilter = "all" | "with" | "without";

// Catálogo paginado en el servidor (1,500+ jugadores en prod).
export const PLAYERS_PAGE_SIZE = 100;

export default function AdminPlayersListPage() {
  const { token, impersonateWithToken, user } = useAuth();
  const { askConfirm, currentSignal } = useAbortableConfirm([]);
  // Soporte Torneos123 (Eduardo 2026-10-30): en Jugadores solo ve y hace
  // «Ver como jugador»; nada que escriba sobre jugadores o sus cuentas (el
  // servidor también lo niega).
  const supportReadOnly = isSupportUser(user);
  const navigate = useNavigate();
  const [players, setPlayers] = useState<PlayerProfile[]>([]);
  const [search, setSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // Resumen agrupado de la última carga masiva (pedido Eduardo: entender
  // los 35 errores, no solo contarlos).
  const [bulkErrorGroups, setBulkErrorGroups] = useState<BulkErrorGroup[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [editingPlayerId, setEditingPlayerId] = useState<number | null>(null);
  const isNarrowViewport = useMediaQuery("(max-width: 640px)");
  const [editForm, setEditForm] = useState<PlayerEditForm | null>(null);
  const [actionPlayerId, setActionPlayerId] = useState<number | null>(null);
  // Fusión de duplicados: el panel se abre solo al pulsar "Fusionar…".
  const [mergeOrigin, setMergeOrigin] = useState<PlayerProfile | null>(null);
  // Acceso propio de un perfil familiar: el panel se abre solo al pulsar su botón.
  const [ownAccessPlayer, setOwnAccessPlayer] = useState<PlayerProfile | null>(null);
  // Columnas (Eduardo 2026-09-11): por default solo Nombre, Apellido
  // Paterno, Usuario, Email y Acciones; "Mostrar todas" enseña las demás,
  // pero solo las que tienen datos en la base.
  const [showAllPlayerColumns, setShowAllPlayerColumns] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [accountFilter, setAccountFilter] = useState<AccountFilter>("all");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  // Paginación y filtro de cuenta viven en el servidor; aquí solo la página actual.
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const pageRef = useRef(0);
  const accountFilterRef = useRef<AccountFilter>("all");
  pageRef.current = page;
  accountFilterRef.current = accountFilter;
  const visiblePlayers = players;
  const columnsWithData = useMemo(() => {
    const has = (pick: (p: (typeof players)[number]) => unknown) => players.some((p) => {
      const value = pick(p);
      return value !== null && value !== undefined && String(value).trim() !== "";
    });
    return new Set<string>([
      ...(has((p) => p.maternal_last_name) ? ["maternal_last_name"] : []),
      ...(has((p) => p.phone) ? ["phone"] : []),
      ...(has((p) => p.birthdate) ? ["birthdate"] : []),
      ...(has((p) => p.nationality_code) ? ["nationality_code"] : []),
      ...(has((p) => p.national_federation_id) ? ["national_federation_id"] : []),
      ...(has((p) => p.wtn_tennis_id) ? ["wtn_tennis_id"] : []),
      ...(has((p) => p.dominant_hand) ? ["dominant_hand"] : []),
      ...(has((p) => p.backhand_type) ? ["backhand_type"] : []),
      ...(has((p) => p.club_name) ? ["club_name"] : []),
      ...(has((p) => p.state_province) ? ["state_province"] : []),
      ...(has((p) => p.city) ? ["city"] : []),
      ...(has((p) => p.gender) ? ["gender"] : []),
      ...(has((p) => p.guardian_name) ? ["guardian_name"] : []),
      ...(has((p) => p.guardian_phone) ? ["guardian_phone"] : []),
      ...(has((p) => p.guardian_relationship) ? ["guardian_relationship"] : []),
      ...(has((p) => p.guardian_email) ? ["guardian_email"] : []),
      ...(has((p) => p.coach_name) ? ["coach_name"] : []),
      ...(has((p) => p.coach_phone) ? ["coach_phone"] : []),
      ...(has((p) => p.coach_email) ? ["coach_email"] : []),
      "data_consent",
    ]);
  }, [players]);
  const BASIC_PLAYER_COLUMNS = ["first_name", "last_name", "username", "email", "actions"];
  const hiddenPlayerColumnCount = useMemo(
    () => (showAllPlayerColumns ? 0 : [...columnsWithData].filter((key) => !BASIC_PLAYER_COLUMNS.includes(key)).length),
    [showAllPlayerColumns, columnsWithData],
  );
  function colClass(key: string): string {
    const visible = BASIC_PLAYER_COLUMNS.includes(key) || (showAllPlayerColumns && columnsWithData.has(key));
    return `col-${key}${visible ? "" : " players-col--hidden"}`;
  }

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [maternalLastName, setMaternalLastName] = useState("");
  const [uniquePlayerId, setUniquePlayerId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [gender, setGender] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [nationalityCode, setNationalityCode] = useState("");
  const [nationalFederationId, setNationalFederationId] = useState("");
  const [wtnTennisId, setWtnTennisId] = useState("");
  const [dominantHand, setDominantHand] = useState("");
  const [backhandType, setBackhandType] = useState("");
  const [clubName, setClubName] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [guardianPhone, setGuardianPhone] = useState("");
  const [guardianRelationship, setGuardianRelationship] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [coachName, setCoachName] = useState("");
  const [coachPhone, setCoachPhone] = useState("");
  const [coachEmail, setCoachEmail] = useState("");
  const [dataConsent, setDataConsent] = useState(false);

  const loadPlayers = useCallback(
    async (term: string) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const filter = accountFilterRef.current;
        const response = await api.players.search(token, {
          search: term || undefined,
          limit: PLAYERS_PAGE_SIZE,
          offset: pageRef.current * PLAYERS_PAGE_SIZE,
          account: filter === "all" ? undefined : filter,
        });
        setPlayers(response.data.players);
        setTotal(response.data.total ?? response.data.players.length);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "No pudimos cargar los jugadores.");
        } else {
          setError("Error de red. Intenta otra vez.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  // Búsqueda al escribir (300 ms de pausa); la primera carga va directa.
  const firstLoadRef = useRef(true);
  useEffect(() => {
    const term = pendingSearch.trim();
    const delay = firstLoadRef.current ? 0 : 300;
    firstLoadRef.current = false;
    const timer = window.setTimeout(() => {
      setSearch(term);
      // Nueva búsqueda: vuelve a la página 1.
      pageRef.current = 0;
      setPage(0);
      void loadPlayers(term);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [pendingSearch, loadPlayers]);

  // Cambio de página o de filtro de cuenta: recarga con la búsqueda vigente.
  const pagingLoadedRef = useRef(false);
  useEffect(() => {
    if (!pagingLoadedRef.current) {
      pagingLoadedRef.current = true;
      return;
    }
    void loadPlayers(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, accountFilter]);

  // Menú «⋯»: Escape o clic fuera lo cierran.
  useEffect(() => {
    if (openMenuId === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenMenuId(null);
    }
    function onPointer(event: MouseEvent) {
      const target = event.target as Element | null;
      if (!target?.closest?.("[data-row-menu]")) setOpenMenuId(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [openMenuId]);

  function resetForm() {
    setFirstName("");
    setLastName("");
    setMaternalLastName("");
    setUniquePlayerId("");
    setPhone("");
    setEmail("");
    setCity("");
    setStateProvince("");
    setGender("");
    setBirthdate("");
    setNationalityCode("");
    setNationalFederationId("");
    setWtnTennisId("");
    setDominantHand("");
    setBackhandType("");
    setClubName("");
    setGuardianName("");
    setGuardianPhone("");
    setGuardianRelationship("");
    setGuardianEmail("");
    setCoachName("");
    setCoachPhone("");
    setCoachEmail("");
    setDataConsent(false);
  }

  function beginEdit(player: PlayerProfile) {
    setMessage(null);
    setError(null);
    setEditingPlayerId(player.id);
    setEditForm(editFormFromPlayer(player));
  }

  // Rediseño 2026-10 (maqueta aprobada): fila compacta con 3 acciones
  // visibles (Generar PIN, Ver como jugador, Editar) y el resto en «⋯».
  // Mismas reglas de permiso que antes: sin cuenta propia del admin no hay
  // botones de cuenta, solo la etiqueta que explica por qué.
  function renderSupportViewAs(p: PlayerProfile) {
    return (
      <button
        type="button"
        className="btn btn--ghost btn--compact"
        onClick={() => void handleViewAsPlayer(p)}
        disabled={actionPlayerId !== null || !p.user_id}
        title={!p.user_id ? "Este jugador aún no tiene cuenta para entrar" : "Ver sistema como jugador"}
      >
        Ver como jugador
      </button>
    );
  }

  function renderMainActions(p: PlayerProfile) {
    const manage = canManagePlayerAccount(p);
    if (supportReadOnly) {
      return (
        <>
          {renderSupportViewAs(p)}
          {renderRowMenu(p)}
        </>
      );
    }
    return (
      <>
        {manage ? (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--compact"
              onClick={() => void handleGeneratePin(p)}
              disabled={actionPlayerId !== null}
            >
              Generar PIN
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--compact"
              onClick={() => void handleViewAsPlayer(p)}
              disabled={actionPlayerId !== null || !p.user_id}
              title={!p.user_id ? "Genera PIN primero para crear el usuario del jugador" : "Ver sistema como jugador"}
            >
              Ver como jugador
            </button>
          </>
        ) : (
          <small className="muted account-not-owned" data-no-toast title={accountNotOwnedNote(accountNotOwnedInfo(p))}>
            {accountNotOwnedLabel(p)}
          </small>
        )}
        <button
          type="button"
          className="btn btn--compact adm-players__edit"
          onClick={() => beginEdit(p)}
          disabled={actionPlayerId !== null}
        >
          Editar
        </button>
        {renderRowMenu(p)}
      </>
    );
  }

  function renderRowMenu(p: PlayerProfile) {
    const open = openMenuId === p.id;
    const manage = canManagePlayerAccount(p);
    const close = () => setOpenMenuId(null);
    return (
      <div className="adm-players__menu" data-row-menu>
        <button
          type="button"
          className="btn btn--ghost btn--compact adm-players__menu-trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Más acciones para ${p.display_name}`}
          onClick={() => setOpenMenuId(open ? null : p.id)}
        >
          ⋯
        </button>
        {open ? (
          <div className="adm-players__menu-list" role="menu">
            <Link role="menuitem" className="adm-players__menu-item" to={routes.adminPlayerProfile(p.id)} onClick={close}>
              Perfil
            </Link>
            {manage && !supportReadOnly ? (
              <>
                <button
                  type="button"
                  role="menuitem"
                  className="adm-players__menu-item"
                  onClick={() => { close(); setMergeOrigin(p); }}
                  disabled={actionPlayerId !== null}
                  title="La misma persona quedó con dos perfiles: júntalos en uno"
                >
                  Fusionar…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="adm-players__menu-item"
                  onClick={() => { close(); setOwnAccessPlayer(p); }}
                  disabled={actionPlayerId !== null}
                  title="Para un hijo que hoy entra con la cuenta de su tutor: su propio usuario, sin quitarle el acceso al tutor"
                >
                  Acceso propio…
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="adm-players__menu-item adm-players__menu-item--danger"
                  onClick={() => { close(); void handleDelete(p); }}
                  disabled={actionPlayerId !== null}
                >
                  Eliminar
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  function renderPlayerIdentity(p: PlayerProfile) {
    const fullName = [p.first_name, p.last_name, p.maternal_last_name].filter(Boolean).join(" ") || `Jugador #${p.id}`;
    const year = p.birthdate ? p.birthdate.slice(0, 4) : null;
    const sexo = p.gender === "male" ? "Masculino" : p.gender === "female" ? "Femenino" : null;
    const sub = [sexo, year].filter(Boolean).join(" · ");
    return (
      <div className="adm-players__who">
        <span className="adm-players__avatar" aria-hidden="true">{playerInitials(p)}</span>
        <span className="adm-players__who-text">
          <Link className="adm-players__name" to={routes.adminPlayerProfile(p.id)} title="Abrir home/perfil del jugador">
            {fullName}
          </Link>
          {sub ? <small className="adm-players__sub">{sub}</small> : null}
        </span>
      </div>
    );
  }

  function renderAccountChip(p: PlayerProfile) {
    return playerHasAccount(p) ? (
      <span className="adm-players__chip adm-players__chip--on">Con cuenta</span>
    ) : (
      <span className="adm-players__chip">Sin cuenta</span>
    );
  }

  function renderCompactTable() {
    return (
      <TableScrollWrap className="adm-players__table-shell" ariaLabel="Catálogo de jugadores">
        <table className="data-table adm-players__table">
          <thead>
            <tr>
              <th className="adm-players__th-who">Jugador</th>
              <th className="adm-players__th-club">Club</th>
              <th className="adm-players__th-account">Cuenta</th>
              <th className="adm-players__th-contact">Contacto</th>
              <th className="adm-players__th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visiblePlayers.map((p) => (
              <tr key={p.id}>
                <td>{renderPlayerIdentity(p)}</td>
                <td className="adm-players__club" title={p.club_name ?? undefined}>{displayValue(p.club_name)}</td>
                <td>{renderAccountChip(p)}</td>
                <td className="adm-players__contact" title={playerContact(p)}>{playerContact(p)}</td>
                <td>
                  <div className="table-actions adm-players__actions">{renderMainActions(p)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollWrap>
    );
  }

  // ≤640 px: una tarjeta por jugador (maqueta de celular) con las mismas
  // acciones; «Editar» abre la hoja con los campos apilados.
  function renderMobilePlayerList() {
    return (
      <div className="mobile-player-list adm-players__cards" aria-label="Catálogo de jugadores">
        {visiblePlayers.map((p) => (
          <article key={p.id} className="adm-players__card">
            <div className="adm-players__card-top">
              {renderPlayerIdentity(p)}
              {renderAccountChip(p)}
            </div>
            <small className="adm-players__card-meta">
              {[p.club_name, playerContact(p)].filter(Boolean).join(" · ")}
            </small>
            <div className="adm-players__actions adm-players__card-actions">{renderMainActions(p)}</div>
          </article>
        ))}
      </div>
    );
  }

  // Hoja de edición: en celular y en la vista compacta (la tabla ancha de
  // «Columnas» conserva la edición en línea).
  function renderEditSheet() {
    if ((!isNarrowViewport && showAllPlayerColumns) || editingPlayerId === null || !editForm) return null;
    const player = players.find((item) => item.id === editingPlayerId);
    const isBusy = actionPlayerId === editingPlayerId;
    // El correo de un jugador que registró otra organización no se cambia
    // aquí: a ese correo llegan sus datos de acceso.
    const emailLocked = !canManagePlayerAccount(player);
    const text = (label: string, field: keyof PlayerEditForm, type = "text") => {
      const locked = field === "email" && emailLocked;
      return (
        <label key={String(field)}>
          {label}
          <input
            type={type}
            aria-label={label}
            value={String(editForm[field] ?? "")}
            onChange={(e) => updateEditField(field, e.target.value as PlayerEditForm[typeof field])}
            disabled={isBusy || locked}
          />
          {locked ? (
            <small className="muted" data-no-toast>
              {accountNotOwnedEmailNote(accountNotOwnedInfo(player))}
            </small>
          ) : null}
        </label>
      );
    };
    return createPortal(
      <div className="result-sheet" role="dialog" aria-modal="true" aria-label="Editar jugador">
        <div className="result-sheet__backdrop" onClick={cancelEdit} />
        <div className="result-sheet__panel player-edit-sheet">
          <header className="result-sheet__header">
            <div>
              <span className="player-pro-eyebrow">Editar jugador</span>
              <strong>{player ? [player.first_name, player.last_name].filter(Boolean).join(" ") : ""}</strong>
            </div>
            <button type="button" className="result-sheet__close" onClick={cancelEdit} aria-label="Cerrar">×</button>
          </header>
          <div className="result-sheet__body player-edit-sheet__body">
            {text("Nombre", "first_name")}
            {text("Apellido paterno", "last_name")}
            {text("Apellido materno", "maternal_last_name")}
            {text("Teléfono", "phone", "tel")}
            {text("Email", "email", "email")}
            {text("Fecha de nacimiento", "birthdate", "date")}
            <label>
              Género
              <select className="t123-select" value={editForm.gender} onChange={(e) => updateEditField("gender", e.target.value)} disabled={isBusy}>
                <option value="">Sin especificar</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
              </select>
            </label>
            {text("Club / Academia", "club_name")}
            {text("Estado / Provincia", "state_province")}
            {text("Ciudad", "city")}
            {text("Nacionalidad (código)", "nationality_code")}
            {text("ID FMT", "national_federation_id")}
            {text("WTN ID", "wtn_tennis_id")}
            <label>
              Mano
              <select className="t123-select" value={editForm.dominant_hand} onChange={(e) => updateEditField("dominant_hand", e.target.value)} disabled={isBusy}>
                <option value="">Sin especificar</option>
                <option value="right">Diestro</option>
                <option value="left">Zurdo</option>
              </select>
            </label>
            <label>
              Revés
              <select className="t123-select" value={editForm.backhand_type} onChange={(e) => updateEditField("backhand_type", e.target.value)} disabled={isBusy}>
                <option value="">Sin especificar</option>
                <option value="one_hand">Una mano</option>
                <option value="two_hands">Dos manos</option>
              </select>
            </label>
            {text("Tutor", "guardian_name")}
            {text("Teléfono del tutor", "guardian_phone", "tel")}
            {text("Parentesco", "guardian_relationship")}
            {text("Email del tutor", "guardian_email", "email")}
            {text("Coach", "coach_name")}
            {text("Teléfono del coach", "coach_phone", "tel")}
            {text("Email del coach", "coach_email", "email")}
            <label className="form__checkbox">
              <input type="checkbox" checked={editForm.data_consent} onChange={(e) => updateEditField("data_consent", e.target.checked)} disabled={isBusy} />
              <span>Consentimiento de datos</span>
            </label>
          </div>
          <footer className="result-sheet__footer">
            <button type="button" className="btn btn--ghost" onClick={cancelEdit} disabled={isBusy}>Cancelar</button>
            <button type="button" className="btn result-sheet__publish" onClick={() => void handleSaveEdit(editingPlayerId)} disabled={isBusy}>
              {isBusy ? "Guardando…" : "✓ Guardar"}
            </button>
          </footer>
        </div>
      </div>,
      document.body,
    );
  }

  function cancelEdit() {
    setEditingPlayerId(null);
    setEditForm(null);
  }

  function updateEditField<K extends keyof PlayerEditForm>(field: K, value: PlayerEditForm[K]) {
    setEditForm((current) => (current ? { ...current, [field]: value } : current));
  }

  async function handleSaveEdit(playerId: number) {
    if (!token || !editForm) return;
    if (!editForm.first_name.trim()) {
      setError("El nombre del jugador es obligatorio.");
      return;
    }
    setActionPlayerId(playerId);
    setError(null);
    setMessage(null);
    try {
      const response = await api.players.update(token, playerId, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim() || null,
        maternal_last_name: editForm.maternal_last_name.trim() || null,
        unique_player_id: editForm.unique_player_id.trim() || null,
        phone: editForm.phone.trim() || null,
        email: editForm.email.trim() || null,
        city: editForm.city.trim() || null,
        state_province: editForm.state_province.trim() || null,
        gender: editForm.gender || null,
        birthdate: editForm.birthdate || null,
        nationality_code: editForm.nationality_code.trim().toUpperCase() || null,
        national_federation_id: editForm.national_federation_id.trim().toUpperCase() || null,
        wtn_tennis_id: editForm.wtn_tennis_id.trim().toUpperCase() || null,
        dominant_hand: editForm.dominant_hand || null,
        backhand_type: editForm.backhand_type || null,
        club_name: editForm.club_name.trim() || null,
        guardian_name: editForm.guardian_name.trim() || null,
        guardian_phone: editForm.guardian_phone.trim() || null,
        guardian_relationship: editForm.guardian_relationship.trim() || null,
        guardian_email: editForm.guardian_email.trim() || null,
        coach_name: editForm.coach_name.trim() || null,
        coach_phone: editForm.coach_phone.trim() || null,
        coach_email: editForm.coach_email.trim() || null,
        data_consent: editForm.data_consent,
        category_id: editForm.category_id ? Number(editForm.category_id) : null,
      });
      setMessage(`Jugador actualizado: ${response.data.player.display_name}.`);
      cancelEdit();
      void loadPlayers(search);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo actualizar el jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setActionPlayerId(null);
    }
  }

  async function handleDelete(player: PlayerProfile) {
    if (!token) return;
    const confirmed = await askConfirm(
      `¿Eliminar a ${player.display_name}? Se archivará del catálogo global y su cuenta de acceso se desactivará, salvo que sea una cuenta familiar que usan otros jugadores.`, { acceptLabel: "Sí, eliminar jugador", tone: "danger" });
    if (!confirmed) return;
    setActionPlayerId(player.id);
    setError(null);
    setMessage(null);
    try {
      const response = await api.players.remove(token, player.id);
      setMessage(archiveSuccessMessage(response.data, player.display_name));
      if (editingPlayerId === player.id) cancelEdit();
      void loadPlayers(search);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo eliminar el jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setActionPlayerId(null);
    }
  }

  async function handleGeneratePin(player: PlayerProfile) {
    if (!token) return;
    const signal = currentSignal();
    setActionPlayerId(player.id);
    setError(null);
    setMessage(null);
    try {
      try {
        const response = await api.players.generatePin(token, player.id);
        setMessage(pinSuccessMessage(response.data, player.display_name));
      } catch (err) {
        const familyConflict = familyAccountPinConflict(err);
        if (!familyConflict) throw err;
        // Cuenta familiar: el PIN le cambiaría la contraseña al tutor. Se
        // explica y se ofrece lo que sí sirve: «Acceso propio», cambiar la
        // contraseña familiar confirmándolo o reactivarla (Eduardo 2026-09-29).
        const outcome = await resolveFamilyAccountPin(token, player, familyConflict, signal);
        if (outcome.kind === "own_access") setOwnAccessPlayer(player);
        if (outcome.kind === "message") setMessage(outcome.text);
      }
      void loadPlayers(search);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo generar el PIN.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setActionPlayerId(null);
    }
  }

  async function handleViewAsPlayer(player: PlayerProfile) {
    if (!token) return;
    if (!player.user_id) {
      setError("Primero genera PIN para crear el usuario del jugador.");
      return;
    }
    const confirmed = await askConfirm(
      `Vas a ver el sistema como ${player.display_name}. Podrás volver a tu sesión admin desde la barra superior.`, { acceptLabel: "Entrar como jugador" });
    if (!confirmed) return;

    setActionPlayerId(player.id);
    setError(null);
    setMessage(null);
    try {
      await impersonatePlayer(token, player.id, impersonateWithToken);
      navigate("/player", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo abrir la vista como jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setActionPlayerId(null);
    }
  }

  async function handleDownloadLayout() {
    if (!token) return;
    try {
      const response = await api.players.downloadLayoutCsv(token);
      if (!response.ok) throw new Error("Error al descargar layout");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "layout_nuevos_jugadores_masivo.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo descargar el layout Excel.");
    }
  }

  async function handleBulkCreate(file: File | null) {
    if (!token || !file) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    setBulkErrorGroups([]);
    try {
      const response = await api.players.bulkCreateCsv(token, file);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || "Error en carga masiva");
      const gruposCreacion = summarizeBulkErrors(data.data.errors ?? []);
      setBulkErrorGroups(gruposCreacion);
      setMessage(
        `Carga masiva completada: ${data.data.created} creados. ${data.data.errors.length} errores.`,
      );
      void loadPlayers(search);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || "Error en carga masiva.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDownloadExport() {
    if (!token) return;
    try {
      const response = await api.players.downloadExportXlsx(token);
      if (!response.ok) throw new Error("Error al exportar");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "jugadores_torneos123.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo descargar el Excel de jugadores.");
    }
  }

  async function handleBulkUpdate(file: File | null) {
    if (!token || !file) return;
    setSubmitting(true);
    setError(null);
    setMessage(null);
    setBulkErrorGroups([]);
    try {
      const response = await api.players.bulkUpdateCsv(token, file);
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error?.message || "Error en actualización masiva");
      const gruposActualizacion = summarizeBulkErrors(data.data.errors ?? []);
      setBulkErrorGroups(gruposActualizacion);
      setMessage(
        `Actualización masiva completada: ${data.data.updated} actualizados. ${data.data.errors.length} errores.`,
      );
      void loadPlayers(search);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message || "Error en actualización masiva.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setMessage(null);
    setError(null);
    // Doble candado ademas del required del navegador: mismo contrato que el
    // wizard de inscripcion (telefono y email obligatorios, gate #145).
    const altaError = altaManualNuevoJugadorError({ firstName, phone, email });
    if (altaError) {
      setError(altaError);
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.players.create(token, {
        first_name: firstName.trim(),
        last_name: lastName.trim() || null,
        maternal_last_name: maternalLastName.trim() || null,
        unique_player_id: uniquePlayerId.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        city: city.trim() || null,
        state_province: stateProvince.trim() || null,
        gender: gender || null,
        birthdate: birthdate || null,
        nationality_code: nationalityCode.trim().toUpperCase() || null,
        national_federation_id: nationalFederationId.trim().toUpperCase() || null,
        wtn_tennis_id: wtnTennisId.trim().toUpperCase() || null,
        dominant_hand: dominantHand || null,
        backhand_type: backhandType || null,
        club_name: clubName.trim() || null,
        guardian_name: guardianName.trim() || null,
        guardian_phone: guardianPhone.trim() || null,
        guardian_relationship: guardianRelationship.trim() || null,
        guardian_email: guardianEmail.trim() || null,
        coach_name: coachName.trim() || null,
        coach_phone: coachPhone.trim() || null,
        coach_email: coachEmail.trim() || null,
        data_consent: dataConsent,
      });
      setMessage(`Jugador creado: ${response.data.player.display_name}.`);
      resetForm();
      void loadPlayers(search);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo crear el jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {mergeOrigin && token ? (
        <PlayerMergePanel
          token={token}
          origin={mergeOrigin}
          onClose={() => setMergeOrigin(null)}
          onMerged={(text) => {
            setMergeOrigin(null);
            setError(null);
            setMessage(text);
            void loadPlayers(search);
          }}
        />
      ) : null}
      {ownAccessPlayer && token ? (
        <PlayerOwnAccessPanel token={token} player={ownAccessPlayer} onClose={() => setOwnAccessPlayer(null)} />
      ) : null}
      <section className="adm-players">
        <header className="adm-players__head">
          <div>
            <span className="adm-players__eyebrow">Catálogo de jugadores</span>
            <h1 className="adm-players__title hint-label">
              Jugadores <span className="adm-players__count">{total.toLocaleString("es-MX")}</span>
              <FieldHint label="Qué es el catálogo de jugadores">
                Catálogo global: cada jugador existe una sola vez y se inscribe a los torneos desde Inscripciones o Jugadores del torneo. Aquí lo buscas, editas sus datos, generas su PIN o entras como él para ver lo que ve.
              </FieldHint>
            </h1>
          </div>
          {supportReadOnly ? (
            <p className="support-money-note" role="note">
              Como {SUPPORT_ROLE_LABEL} aquí solo ves a los jugadores y puedes «Ver como jugador». Altas, cambios,
              PIN y fusiones los hace el organizador (o tú, desde las inscripciones de su torneo).
            </p>
          ) : null}
          {supportReadOnly ? null : (
          <div className="adm-players__head-actions">
            <button
              type="button"
              className="adm-players__btn adm-players__btn--secondary"
              aria-expanded={showImport}
              aria-controls="adm-players-import"
              onClick={() => setShowImport((v) => !v)}
            >
              Excel e importación
            </button>
            <button
              type="button"
              className="adm-players__btn adm-players__btn--primary"
              aria-expanded={showCreate}
              aria-controls="adm-players-create"
              onClick={() => setShowCreate((v) => !v)}
            >
              + Nuevo jugador
            </button>
          </div>
          )}
        </header>

        {showImport ? (
          <div className="adm-players__import" id="adm-players-import">
            <button type="button" className="adm-players__import-item" onClick={() => void handleDownloadExport()}>
              <strong>Descargar base</strong>
              <small>Excel con todos los jugadores</small>
            </button>
            <label className="adm-players__import-item">
              <strong>Actualizar en masa</strong>
              <small>Sube la base corregida (Excel o CSV)</small>
              <input
                type="file"
                accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(e) => void handleBulkUpdate(e.target.files?.[0] ?? null)}
                style={{ display: "none" }}
              />
            </label>
            <button type="button" className="adm-players__import-item" onClick={() => void handleDownloadLayout()}>
              <strong>Formato de carga</strong>
              <small>Plantilla para jugadores nuevos</small>
            </button>
            <label className="adm-players__import-item adm-players__import-item--lime">
              <strong>Cargar nuevos</strong>
              <small>Sube la plantilla llena</small>
              <input
                type="file"
                accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                onChange={(e) => void handleBulkCreate(e.target.files?.[0] ?? null)}
                style={{ display: "none" }}
              />
            </label>
            <p className="adm-players__import-hint">
              Descargar base: exporta el catálogo completo con todas las columnas que tienen datos. Actualizar en masa: sube ese mismo archivo editado para corregir jugadores existentes (se cruza por ID). Formato de carga: plantilla vacía para dar de alta muchos jugadores. Cargar nuevos: sube esa plantilla llena; los renglones con error se reportan sin bloquear a los demás.
            </p>
          </div>
        ) : null}

        {showCreate ? (
          <div className="card adm-players__create" id="adm-players-create">
            <h2 className="adm-players__create-title hint-label">
              Nuevo jugador
              <FieldHint label="Cómo funciona el alta de jugador">
                Registra un jugador en el catálogo global (no lo inscribe a ningún torneo). Su usuario de acceso será su correo o su usuario corto; si no tiene acceso todavía, genérale un PIN de un solo uso desde la lista.
              </FieldHint>
            </h2>
          <form className="form" onSubmit={handleCreate}>
            <label>
              Nombre
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Apellido Paterno
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Apellido Materno
              <input
                type="text"
                value={maternalLastName}
                onChange={(e) => setMaternalLastName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Teléfono
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Fecha de nacimiento (opcional)
              <input
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Nacionalidad — código ISO (opcional)
              <input
                type="text"
                value={nationalityCode}
                onChange={(e) => setNationalityCode(e.target.value)}
                disabled={submitting}
                maxLength={3}
                placeholder="MEX, USA, ESP..."
              />
            </label>
            <label>
              ID Federativo / FMT (opcional)
              <input
                type="text"
                value={nationalFederationId}
                onChange={(e) => setNationalFederationId(e.target.value)}
                disabled={submitting}
                maxLength={120}
              />
            </label>
            <label>
              WTN Tennis ID (opcional)
              <input
                type="text"
                value={wtnTennisId}
                onChange={(e) => setWtnTennisId(e.target.value)}
                disabled={submitting}
                maxLength={120}
              />
            </label>
            <label>
              Mano dominante (opcional)
              <select className="t123-select"
                value={dominantHand}
                onChange={(e) => setDominantHand(e.target.value)}
                disabled={submitting}
              >
                <option value="">Sin especificar</option>
                <option value="right">Diestro</option>
                <option value="left">Zurdo</option>
              </select>
            </label>
            <label>
              Tipo de revés (opcional)
              <select className="t123-select"
                value={backhandType}
                onChange={(e) => setBackhandType(e.target.value)}
                disabled={submitting}
              >
                <option value="">Sin especificar</option>
                <option value="one_hand">Una mano</option>
                <option value="two_hands">Dos manos</option>
              </select>
            </label>
            <label>
              Club / Academia (opcional)
              <input
                type="text"
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Estado / Provincia (opcional)
              <input
                type="text"
                value={stateProvince}
                onChange={(e) => setStateProvince(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Ciudad (opcional)
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={submitting}
              />
            </label>
            <label>
              Género (opcional)
              <select className="t123-select"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={submitting}
              >
                <option value="">Sin especificar</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
              </select>
            </label>
            <details className="group-accordion group-accordion--nested">
              <summary className="group-accordion__summary">
                <span className="group-accordion__title-wrap">
                  <span className="group-accordion__title">Tutor legal (si es menor de edad)</span>
                </span>
                <span className="group-accordion__chevron" aria-hidden="true">▾</span>
              </summary>
              <div className="group-accordion__body">
                <label>
                  Nombre del tutor
                  <input
                    type="text"
                    value={guardianName}
                    onChange={(e) => setGuardianName(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label>
                  Teléfono del tutor
                  <input
                    type="tel"
                    value={guardianPhone}
                    onChange={(e) => setGuardianPhone(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label>
                  Parentesco
                  <input
                    type="text"
                    value={guardianRelationship}
                    onChange={(e) => setGuardianRelationship(e.target.value)}
                    disabled={submitting}
                    placeholder="Padre, Madre, Tutor..."
                  />
                </label>
                <label>
                  Email del tutor
                  <input
                    type="email"
                    value={guardianEmail}
                    onChange={(e) => setGuardianEmail(e.target.value)}
                    disabled={submitting}
                  />
                </label>
              </div>
            </details>
            <details className="group-accordion group-accordion--nested">
              <summary className="group-accordion__summary">
                <span className="group-accordion__title-wrap">
                  <span className="group-accordion__title">Profesor / coach</span>
                </span>
                <span className="group-accordion__chevron" aria-hidden="true">▾</span>
              </summary>
              <div className="group-accordion__body">
                <label>
                  Nombre del profesor
                  <input
                    type="text"
                    value={coachName}
                    onChange={(e) => setCoachName(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label>
                  Teléfono del profesor
                  <input
                    type="tel"
                    value={coachPhone}
                    onChange={(e) => setCoachPhone(e.target.value)}
                    disabled={submitting}
                  />
                </label>
                <label>
                  Email del profesor
                  <input
                    type="email"
                    value={coachEmail}
                    onChange={(e) => setCoachEmail(e.target.value)}
                    disabled={submitting}
                  />
                </label>
              </div>
            </details>
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={dataConsent}
                onChange={(e) => setDataConsent(e.target.checked)}
                disabled={submitting}
              />
              <span>Consentimiento de datos — autoriza envío de datos personales a ITF/WTN</span>
            </label>
            <div className="form__actions">
              <button className="btn" type="submit" disabled={submitting}>
                {submitting ? "Creando…" : "Crear jugador"}
              </button>
            </div>
          </form>
          </div>
        ) : null}

        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
        {bulkErrorGroups.length > 0 && (
          <div className="bulk-error-summary" role="status">
            <strong>Qué hay que corregir en el archivo</strong>
            <ul>
              {bulkErrorGroups.map((group) => (
                <li key={group.label}>{bulkErrorGroupLine(group)}</li>
              ))}
            </ul>
            <small>
              Corrige esas columnas en tu archivo y vuelve a cargarlo; las filas que sí entraron
              no se duplican.
            </small>
          </div>
        )}

        <div className="adm-players__panel">
          <div className="adm-players__toolbar">
            <label className="adm-players__search">
              <span className="sr-only">Buscar jugador</span>
              <span aria-hidden="true">🔍</span>
              <input
                type="search"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                placeholder="Buscar jugador: nombre, usuario, email o teléfono"
                aria-label="Buscar jugador"
              />
            </label>
            <div className="adm-players__chips" role="group" aria-label="Filtrar por cuenta">
              {([
                ["all", "Todos"],
                ["with", "Con cuenta"],
                ["without", "Sin cuenta"],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`adm-players__filter${accountFilter === value ? " is-active" : ""}`}
                  aria-pressed={accountFilter === value}
                  onClick={() => {
                    if (value === accountFilter) return;
                    setAccountFilter(value);
                    setPage(0);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {!isNarrowViewport ? (
              <button
                type="button"
                className="adm-players__filter adm-players__filter--cols"
                onClick={() => setShowAllPlayerColumns((current) => !current)}
                aria-pressed={showAllPlayerColumns}
                title={showAllPlayerColumns ? "Volver a la vista compacta" : "Mostrar también las columnas que tienen datos (teléfono, nacimiento, tutor, coach…)"}
              >
                {showAllPlayerColumns ? "Vista compacta" : `Columnas${hiddenPlayerColumnCount ? ` (+${hiddenPlayerColumnCount})` : ""}`}
              </button>
            ) : null}
          </div>

          {!loading && !supportReadOnly && players.some((player) => !canManagePlayerAccount(player)) ? (
            // Aviso fijo (no un toast): explica por qué algunos jugadores no
            // traen los botones de su cuenta (Opción A, Eduardo 2026-09-30).
            <p className="muted account-not-owned-note" role="note" data-no-toast>
              {accountNotOwnedListNote(players)}
            </p>
          ) : null}

          {loading && players.length === 0 ? (
            <p className="muted">Cargando…</p>
          ) : visiblePlayers.length === 0 ? (
            <p className="muted">
              {search || accountFilter !== "all"
                ? "Sin resultados para tu búsqueda."
                : "Aún no hay jugadores registrados."}
            </p>
          ) : isNarrowViewport ? (
            renderMobilePlayerList()
          ) : !showAllPlayerColumns ? (
            renderCompactTable()
          ) : (
            <TableScrollWrap className="global-players-table-shell global-players-table-shell--catalog" ariaLabel="Catálogo de jugadores">
              <table
                className={`data-table global-players-table${showAllPlayerColumns ? "" : " global-players-table--basic"}`}
              >
                <thead>
                  <tr>
                    <th className={colClass("first_name")}>Nombre</th>
                    <th className={colClass("last_name")}>Apellido Paterno</th>
                    <th className={colClass("maternal_last_name")}>Apellido Materno</th>
                    <th className={colClass("phone")}>Teléfono</th>
                    <th className={colClass("username")}>Usuario</th>
                    <th className={colClass("email")}>Email</th>
                    <th className={colClass("birthdate")}>Nacimiento</th>
                    <th className={colClass("nationality_code")}>Nacionalidad</th>
                    <th className={colClass("national_federation_id")}>ID FMT</th>
                    <th className={colClass("wtn_tennis_id")}>WTN ID</th>
                    <th className={colClass("dominant_hand")}>Mano</th>
                    <th className={colClass("backhand_type")}>Revés</th>
                    <th className={colClass("club_name")}>Club / Academia</th>
                    <th className={colClass("state_province")}>Estado / Provincia</th>
                    <th className={colClass("city")}>Ciudad</th>
                    <th className={colClass("gender")}>Género</th>
                    <th className={colClass("guardian_name")}>Tutor</th>
                    <th className={colClass("guardian_phone")}>Tel. tutor</th>
                    <th className={colClass("guardian_relationship")}>Parentesco</th>
                    <th className={colClass("guardian_email")}>Email tutor</th>
                    <th className={colClass("coach_name")}>Coach</th>
                    <th className={colClass("coach_phone")}>Tel. coach</th>
                    <th className={colClass("coach_email")}>Email coach</th>
                    <th className={colClass("data_consent")}>Consentimiento</th>
                    <th className={colClass("actions")}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlayers.map((p) => {
                    const isEditing = editingPlayerId === p.id && editForm;
                    const isBusy = actionPlayerId === p.id;
                    return (
                      <tr key={p.id}>
                        <td className={colClass("first_name")}>
                          {isEditing ? (
                            <input
                              aria-label="Nombre"
                              value={editForm.first_name}
                              onChange={(e) => updateEditField("first_name", e.target.value)}
                              required
                            />
                          ) : (
                            <Link
                              className="player-name-action"
                              to={routes.adminPlayerProfile(p.id)}
                              title="Abrir home/perfil del jugador"
                            >
                              <strong>{p.first_name}</strong>
                            </Link>
                          )}
                        </td>
                        <td className={colClass("last_name")}>
                          {isEditing ? (
                            <input
                              aria-label="Apellido Paterno"
                              value={editForm.last_name}
                              onChange={(e) => updateEditField("last_name", e.target.value)}
                            />
                          ) : (
                            displayValue(p.last_name)
                          )}
                        </td>
                        <td className={colClass("maternal_last_name")}>
                          {isEditing ? (
                            <input
                              aria-label="Apellido Materno"
                              value={editForm.maternal_last_name}
                              onChange={(e) => updateEditField("maternal_last_name", e.target.value)}
                            />
                          ) : (
                            displayValue(p.maternal_last_name)
                          )}
                        </td>
                        <td className={colClass("phone")}>
                          {isEditing ? (
                            <input
                              aria-label="Teléfono"
                              type="tel"
                              value={editForm.phone}
                              onChange={(e) => updateEditField("phone", e.target.value)}
                            />
                          ) : (
                            displayValue(p.phone)
                          )}
                        </td>
                        <td className={colClass("username")}>{displayValue(p.username)}</td>
                        <td className={colClass("email")}>
                          {isEditing ? (
                            <input
                              aria-label="Email"
                              type="email"
                              value={editForm.email}
                              onChange={(e) => updateEditField("email", e.target.value)}
                              disabled={!canManagePlayerAccount(p)}
                              title={canManagePlayerAccount(p) ? undefined : accountNotOwnedEmailNote(accountNotOwnedInfo(p))}
                            />
                          ) : (
                            displayValue(p.email)
                          )}
                        </td>
                        <td className={colClass("birthdate")}>
                          {isEditing ? (
                            <input
                              aria-label="Fecha de nacimiento"
                              type="date"
                              value={editForm.birthdate}
                              onChange={(e) => updateEditField("birthdate", e.target.value)}
                            />
                          ) : (
                            formatDate(p.birthdate)
                          )}
                        </td>
                        <td className={colClass("nationality_code")}>
                          {isEditing ? (
                            <input
                              aria-label="Nacionalidad"
                              value={editForm.nationality_code}
                              onChange={(e) => updateEditField("nationality_code", e.target.value)}
                              maxLength={3}
                            />
                          ) : (
                            displayValue(p.nationality_code)
                          )}
                        </td>
                        <td className={colClass("national_federation_id")}>
                          {isEditing ? (
                            <input
                              aria-label="ID Federativo / FMT"
                              value={editForm.national_federation_id}
                              onChange={(e) => updateEditField("national_federation_id", e.target.value)}
                              maxLength={120}
                            />
                          ) : (
                            displayValue(p.national_federation_id)
                          )}
                        </td>
                        <td className={colClass("wtn_tennis_id")}>
                          {isEditing ? (
                            <input
                              aria-label="WTN Tennis ID"
                              value={editForm.wtn_tennis_id}
                              onChange={(e) => updateEditField("wtn_tennis_id", e.target.value)}
                              maxLength={120}
                            />
                          ) : (
                            displayValue(p.wtn_tennis_id)
                          )}
                        </td>
                        <td className={colClass("dominant_hand")}>
                          {isEditing ? (
                            <select className="t123-select"
                              aria-label="Mano dominante"
                              value={editForm.dominant_hand}
                              onChange={(e) => updateEditField("dominant_hand", e.target.value)}
                            >
                              <option value="">Sin especificar</option>
                              <option value="right">Diestro</option>
                              <option value="left">Zurdo</option>
                            </select>
                          ) : (
                            formatHandLabel(p.dominant_hand)
                          )}
                        </td>
                        <td className={colClass("backhand_type")}>
                          {isEditing ? (
                            <select className="t123-select"
                              aria-label="Tipo de revés"
                              value={editForm.backhand_type}
                              onChange={(e) => updateEditField("backhand_type", e.target.value)}
                            >
                              <option value="">Sin especificar</option>
                              <option value="one_hand">Una mano</option>
                              <option value="two_hands">Dos manos</option>
                            </select>
                          ) : (
                            formatBackhandLabel(p.backhand_type)
                          )}
                        </td>
                        <td className={colClass("club_name")}>
                          {isEditing ? (
                            <input
                              aria-label="Club / Academia"
                              value={editForm.club_name}
                              onChange={(e) => updateEditField("club_name", e.target.value)}
                            />
                          ) : (
                            displayValue(p.club_name)
                          )}
                        </td>
                        <td className={colClass("state_province")}>
                          {isEditing ? (
                            <input
                              aria-label="Estado / Provincia"
                              value={editForm.state_province}
                              onChange={(e) => updateEditField("state_province", e.target.value)}
                            />
                          ) : (
                            displayValue(p.state_province)
                          )}
                        </td>
                        <td className={colClass("city")}>
                          {isEditing ? (
                            <input
                              aria-label="Ciudad"
                              value={editForm.city}
                              onChange={(e) => updateEditField("city", e.target.value)}
                            />
                          ) : (
                            displayValue(p.city)
                          )}
                        </td>
                        <td className={colClass("gender")}>
                          {isEditing ? (
                            <select className="t123-select"
                              aria-label="Género"
                              value={editForm.gender}
                              onChange={(e) => updateEditField("gender", e.target.value)}
                            >
                              <option value="">Sin especificar</option>
                              <option value="male">Masculino</option>
                              <option value="female">Femenino</option>
                            </select>
                          ) : (
                            formatGenderLabel(p.gender)
                          )}
                        </td>
                        <td className={colClass("guardian_name")}>
                          {isEditing ? (
                            <input
                              aria-label="Nombre del tutor"
                              value={editForm.guardian_name}
                              onChange={(e) => updateEditField("guardian_name", e.target.value)}
                            />
                          ) : (
                            displayValue(p.guardian_name)
                          )}
                        </td>
                        <td className={colClass("guardian_phone")}>
                          {isEditing ? (
                            <input
                              aria-label="Teléfono del tutor"
                              type="tel"
                              value={editForm.guardian_phone}
                              onChange={(e) => updateEditField("guardian_phone", e.target.value)}
                            />
                          ) : (
                            displayValue(p.guardian_phone)
                          )}
                        </td>
                        <td className={colClass("guardian_relationship")}>
                          {isEditing ? (
                            <input
                              aria-label="Parentesco"
                              value={editForm.guardian_relationship}
                              onChange={(e) => updateEditField("guardian_relationship", e.target.value)}
                            />
                          ) : (
                            displayValue(p.guardian_relationship)
                          )}
                        </td>
                        <td className={colClass("guardian_email")}>
                          {isEditing ? (
                            <input
                              aria-label="Email del tutor"
                              type="email"
                              value={editForm.guardian_email}
                              onChange={(e) => updateEditField("guardian_email", e.target.value)}
                            />
                          ) : (
                            displayValue(p.guardian_email)
                          )}
                        </td>
                        <td className={colClass("coach_name")}>
                          {isEditing ? (
                            <input
                              aria-label="Nombre del coach"
                              value={editForm.coach_name}
                              onChange={(e) => updateEditField("coach_name", e.target.value)}
                            />
                          ) : (
                            displayValue(p.coach_name)
                          )}
                        </td>
                        <td className={colClass("coach_phone")}>
                          {isEditing ? (
                            <input
                              aria-label="Teléfono del coach"
                              type="tel"
                              value={editForm.coach_phone}
                              onChange={(e) => updateEditField("coach_phone", e.target.value)}
                            />
                          ) : (
                            displayValue(p.coach_phone)
                          )}
                        </td>
                        <td className={colClass("coach_email")}>
                          {isEditing ? (
                            <input
                              aria-label="Email del coach"
                              type="email"
                              value={editForm.coach_email}
                              onChange={(e) => updateEditField("coach_email", e.target.value)}
                            />
                          ) : (
                            displayValue(p.coach_email)
                          )}
                        </td>
                        <td className={colClass("data_consent")}>
                          {isEditing ? (
                            <label className="form-checkbox form-checkbox--compact">
                              <input
                                aria-label="Consentimiento de datos"
                                type="checkbox"
                                checked={editForm.data_consent}
                                onChange={(e) => updateEditField("data_consent", e.target.checked)}
                              />
                              <span>Sí</span>
                            </label>
                          ) : (
                            formatConsentLabel(p.data_consent)
                          )}
                        </td>
                        <td className={colClass("actions")}>
                          <div className="table-actions">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  className="btn btn--compact"
                                  onClick={() => void handleSaveEdit(p.id)}
                                  disabled={isBusy}
                                >
                                  Guardar
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact"
                                  onClick={cancelEdit}
                                  disabled={isBusy}
                                >
                                  Cancelar
                                </button>
                              </>
                            ) : (
                              <>
                                <Link
                                  className="btn btn--ghost btn--compact"
                                  to={routes.adminPlayerProfile(p.id)}
                                >
                                  Perfil
                                </Link>
                                {supportReadOnly ? renderSupportViewAs(p) : (
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact"
                                  onClick={() => beginEdit(p)}
                                  disabled={actionPlayerId !== null}
                                >
                                  Editar
                                </button>
                                )}
                                {supportReadOnly ? null : canManagePlayerAccount(p) ? (
                                <>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact"
                                  onClick={() => void handleGeneratePin(p)}
                                  disabled={actionPlayerId !== null}
                                >
                                  Generar PIN
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact"
                                  onClick={() => void handleViewAsPlayer(p)}
                                  disabled={actionPlayerId !== null || !p.user_id}
                                  title={
                                    !p.user_id
                                      ? "Genera PIN primero para crear el usuario del jugador"
                                      : "Ver sistema como jugador"
                                  }
                                >
                                  Ver como jugador
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact"
                                  onClick={() => setMergeOrigin(p)}
                                  disabled={actionPlayerId !== null}
                                  title="La misma persona quedó con dos perfiles: júntalos en uno"
                                >
                                  Fusionar…
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact"
                                  onClick={() => setOwnAccessPlayer(p)}
                                  disabled={actionPlayerId !== null}
                                  title="Para un hijo que hoy entra con la cuenta de su tutor: su propio usuario, sin quitarle el acceso al tutor"
                                >
                                  Acceso propio…
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--compact btn--danger"
                                  onClick={() => void handleDelete(p)}
                                  disabled={actionPlayerId !== null}
                                >
                                  Eliminar
                                </button>
                                </>
                                ) : (
                                  // Lo registró otra organización: sin botones
                                  // de cuenta; el aviso fijo de arriba explica.
                                  <small className="muted account-not-owned" data-no-toast title={accountNotOwnedNote(accountNotOwnedInfo(p))}>
                                    {accountNotOwnedLabel(p)}
                                  </small>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScrollWrap>
          )}

          {visiblePlayers.length > 0 ? (
            <nav className="adm-players__footer" aria-label="Paginación de jugadores">
              <p className="muted">
                {loading ? "Actualizando… " : ""}
                {`Mostrando ${(page * PLAYERS_PAGE_SIZE + 1).toLocaleString("es-MX")}–${(page * PLAYERS_PAGE_SIZE + visiblePlayers.length).toLocaleString("es-MX")} de ${total.toLocaleString("es-MX")}`}
              </p>
              {total > PLAYERS_PAGE_SIZE ? (
                <div className="adm-players__pager">
                  <button
                    type="button"
                    className="adm-players__page-btn"
                    disabled={loading || page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    ← Anterior
                  </button>
                  <span className="adm-players__page-num">
                    Página {page + 1} de {Math.max(1, Math.ceil(total / PLAYERS_PAGE_SIZE))}
                  </span>
                  <button
                    type="button"
                    className="adm-players__page-btn adm-players__page-btn--next"
                    disabled={loading || (page + 1) * PLAYERS_PAGE_SIZE >= total}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Siguiente →
                  </button>
                </div>
              ) : null}
            </nav>
          ) : null}
        </div>
      </section>
      {renderEditSheet()}
    </>
  );
}
